import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Timer, Flag, CheckCircle, Circle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { STATECRAFT_LOGO } from "@/lib/brand";

export default function MCQTest() {
    const { sessionId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [session, setSession] = useState(location.state || null);
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [review, setReview] = useState(new Set());
    const [timeLeft, setTimeLeft] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const startTime = useRef(Date.now());

    useEffect(() => {
        if (!session) {
            (async () => {
                try {
                    await api.get(`/mcq/session/${sessionId}/result`);
                    navigate(`/mcq/result/${sessionId}`, { replace: true });
                } catch {
                    toast.error("Session not found. Start a new test.");
                    navigate("/mcq", { replace: true });
                }
            })();
        } else {
            setTimeLeft(session.duration_minutes * 60);
        }
        // eslint-disable-next-line
    }, [session]);

    useEffect(() => {
        if (!session) return;
        const id = setInterval(() => {
            setTimeLeft((t) => {
                if (t <= 1) {
                    clearInterval(id);
                    handleSubmit(true);
                    return 0;
                }
                return t - 1;
            });
        }, 1000);
        return () => clearInterval(id);
        // eslint-disable-next-line
    }, [session]);

    if (!session) return <div className="min-h-screen grid place-items-center bg-white text-slate-500">Loading…</div>;

    const questions = session.questions;
    const q = questions[current];

    const selectOption = (idx) => setAnswers((a) => ({ ...a, [q.question_id]: idx }));
    const toggleReview = () => {
        const s = new Set(review);
        s.has(q.question_id) ? s.delete(q.question_id) : s.add(q.question_id);
        setReview(s);
    };

    const goTo = (i) => setCurrent(Math.max(0, Math.min(questions.length - 1, i)));

    const handleSubmit = async (auto = false) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const timeTaken = Math.round((Date.now() - startTime.current) / 1000);
            await api.post(`/mcq/session/${sessionId}/submit`, {
                answers,
                marked_for_review: Array.from(review),
                time_taken_seconds: timeTaken,
            });
            if (auto) toast.warning("Time's up! Submitting…");
            else toast.success("Answers submitted");
            navigate(`/mcq/result/${sessionId}`, { replace: true });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
    const answeredCount = Object.keys(answers).length;

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Top bar */}
            <header className="border-b border-[#BFD3FF] bg-white/95 backdrop-blur sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 py-3 grid grid-cols-3 items-center gap-4">
                    <div className="text-sm truncate flex items-center gap-3">
                        <button
                            data-testid="exit-test-btn"
                            onClick={() => {
                                if (window.confirm("Exit the test? Your progress will be lost.")) navigate("/mcq");
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded-md border border-[#BFD3FF] hover:border-red-300"
                            title="Exit test"
                        >
                            <ArrowLeft size={14} weight="bold" /> Exit
                        </button>
                        <div className="truncate">
                            <span className="text-slate-500">Test:</span>
                            <span className="ml-2 font-semibold uppercase text-slate-800">{session.exam_id.replace("_", " ")}</span>
                            <span className="mx-2 text-slate-400">/</span>
                            <span className="text-[#1E3A8A] font-semibold">{session.subject}</span>
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                        <img src={STATECRAFT_LOGO} alt="Statecraft" className="h-16 sm:h-20 w-auto" />
                        <div className="text-[9px] uppercase tracking-[0.28em] text-slate-500">Chintana · KPSC · KEA</div>
                    </div>
                    <div className="flex items-center justify-end gap-3">
                        <div className={`flex items-center gap-2 font-mono text-lg px-3 py-1.5 rounded-lg ${timeLeft < 60 ? "bg-red-50 text-red-600 border border-red-200" : "bg-[#1E3A8A]/5 text-[#1E3A8A] border border-[#1E3A8A]/20"}`} data-testid="timer">
                            <Timer size={18} weight="duotone" />
                            {fmt(timeLeft)}
                        </div>
                        <button data-testid="submit-test-btn" onClick={() => handleSubmit(false)} disabled={submitting} className="btn-primary text-sm py-2 disabled:opacity-60">
                            Submit ({answeredCount}/{questions.length})
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-12 gap-6">
                {/* Question area */}
                <div className="lg:col-span-8 bg-white border border-[#BFD3FF] rounded-2xl p-6 sm:p-8 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <div className="text-sm text-slate-500">
                            Question <span className="font-mono text-[#1E3A8A] text-base font-bold">{current + 1}</span>
                            <span className="text-slate-400 mx-1.5">of</span>
                            <span className="font-mono">{questions.length}</span>
                        </div>
                        <button data-testid="review-toggle-btn" onClick={toggleReview} className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border ${review.has(q.question_id) ? "border-gold text-amber-800 bg-gold/10" : "border-[#BFD3FF] text-slate-600"}`}>
                            <Flag size={14} weight={review.has(q.question_id) ? "fill" : "regular"} /> Mark for review
                        </button>
                    </div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">{q.subject} · {q.difficulty}</div>
                    <h2 className="font-heading text-xl sm:text-2xl leading-snug mb-6 text-slate-900" data-testid="question-text">{q.question}</h2>
                    <div className="space-y-3">
                        {q.options.map((opt, i) => {
                            const active = answers[q.question_id] === i;
                            return (
                                <button
                                    key={i}
                                    data-testid={`option-${i}`}
                                    onClick={() => selectOption(i)}
                                    className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-colors flex items-start gap-3 ${
                                        active ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-[#BFD3FF] hover:border-[#1E3A8A] bg-white"
                                    }`}
                                >
                                    <div className={`shrink-0 w-8 h-8 rounded-lg grid place-items-center font-mono text-xs font-bold ${active ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600"}`}>
                                        {String.fromCharCode(65 + i)}
                                    </div>
                                    <div className="text-slate-800 text-base">{opt}</div>
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-8 flex justify-between">
                        <button data-testid="prev-btn" onClick={() => goTo(current - 1)} disabled={current === 0} className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">
                            ← Previous
                        </button>
                        <button data-testid="next-btn" onClick={() => goTo(current + 1)} disabled={current === questions.length - 1} className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">
                            Next →
                        </button>
                    </div>
                </div>

                {/* Navigator */}
                <aside className="lg:col-span-4 space-y-4">
                    <div className="bento-card">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Navigator</div>
                        <div className="grid grid-cols-6 gap-2">
                            {questions.map((qq, i) => {
                                const isAnswered = answers[qq.question_id] !== undefined;
                                const isReview = review.has(qq.question_id);
                                const isCurrent = i === current;
                                const state = isReview ? "review" : isAnswered ? "answered" : "unanswered";
                                return (
                                    <button
                                        key={qq.question_id}
                                        onClick={() => setCurrent(i)}
                                        data-testid={`nav-${i}`}
                                        className={`navcell navcell-${state} ${isCurrent ? "navcell-current" : ""}`}
                                    >
                                        {i + 1}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-4 space-y-1.5 text-xs text-slate-500">
                            <div className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" weight="fill" /> Answered</div>
                            <div className="flex items-center gap-2"><Flag size={14} className="text-gold" weight="fill" /> Marked for review</div>
                            <div className="flex items-center gap-2"><Circle size={14} className="text-slate-400" /> Unanswered</div>
                        </div>
                    </div>
                    <div className="bento-card">
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Answered</span>
                            <span className="font-mono text-slate-800">{answeredCount}/{questions.length}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full mt-2 overflow-hidden">
                            <div className="h-full bg-[#1E3A8A]" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}
